import React, { useState } from "react";
import FlatCard from "components/UI-lib/FlatCard";
import { classNames } from "components/utils";
import Chip from "../chips";
import { CpuChipIcon, PlayCircleIcon } from "@heroicons/react/24/outline";
import {
  CircleStackIcon,
  CpuChipIcon as SolidCpuChipIcon,
  CloudIcon,
  ServerIcon,
} from "@heroicons/react/24/solid";
import { Badge } from "components/Graphs/Badge";
import { GPUInstanceType } from "components/Environment/Settings/Tabs/Compute/InstanceChanger/GPUTypes";
import {
  displayPlatformType,
  getCloudPlatformUrl,
} from "components/Environment/utils";
import CloudDisplay from "components/UI-lib/CloudDisplay";
import { Button } from "components/Graphs/Button";
import { cx } from "components/Graphs/utils";
import { motion } from "framer-motion";

interface GPUFlatCardProps {
  includeTitle?: boolean;
  setInstanceType: (instanceType: GPUInstanceType) => void;
  instanceType: GPUInstanceType;
  chipName: string;
  manufacturerName: string;
  gpuCount: number;
  vramSize: string;
  ramSize: string;
  cpuCount: number;
  cloudProvider: string;
  cloud: string | undefined;
  price: string;
  withChipIcon?: boolean;
}

const RackCard: React.FC<GPUFlatCardProps> = ({
  includeTitle,
  instanceType,
  setInstanceType,
  chipName,
  manufacturerName,
  gpuCount,
  vramSize,
  ramSize,
  cpuCount,
  cloudProvider,
  cloud,
  price,
  withChipIcon,
}) => {
  const [hoveredRack, setHoveredRack] = useState<boolean>(false);
  const instanceStorage = instanceType.supported_storage.map((storage) => {
    const supportedStorage = { ...storage };
    if (storage.type.includes("ssd")) {
      supportedStorage.type_label = "SSD";
    } else if (storage.type.includes("nvme")) {
      supportedStorage.type_label = "NVME";
    }
    return supportedStorage;
  });

  return (
    <Button
      variant="noshow"
      onClick={() => {
        setInstanceType(instanceType);
      }}
      className="w-full"
    >
      <motion.div
        className="w-full min-h-[100px] px-2 py-3 rounded-md border-zinc-800 border-2 transition-shadow duration-200 hover:shadow-xl hover:border-highlight hover:border-1"
        key={instanceType.type}
        whileHover={{
          scale: 1.02,
          transition: { duration: 0.2, ease: "easeOut" },
        }}
        style={{
          background: `
            repeating-linear-gradient(
              0deg,
              #1a1a1a 0px,
              #1a1a1a 1px,
              #2a2a2a 1px,
              #2a2a2a 2px
            ),
            repeating-linear-gradient(
              90deg,
              #1a1a1a 0px,
              #1a1a1a 1px,
              #2a2a2a 1px,
              #2a2a2a 2px
            )
          `,
          boxShadow: `
            0 20px 40px rgba(0, 0, 0, 0.6),
            inset 0 0 20px rgba(0, 0, 0, 0.5),
            inset 0 1px 0 rgba(255, 255, 255, 0.1)
          `,
        }}
      >
        <div className="flex flex-row">
          {includeTitle && (
            <div className="flex flex-row items-center mb-1">
              <CpuChipIcon className="text-white h-5 w-5 mr-1" />
              <div className="text-white">Compute</div>
            </div>
          )}
          <div className="flex flex-col items-center gap-2 mr-2 justify-center">
            <StatusNob count={`${gpuCount}x`} />
            <div className="bg-primaryBg rounded-md w-6 h-2"></div>
            <div className="bg-primaryBg rounded-md w-6 h-2"></div>
          </div>
          <div className="flex flex-col justify-center mt-2">
            <SocketDiv className="flex flex-row mr-4 py-3">
              <div className="flex flex-col justify-center mr-3">
                <div className="flex">
                  {withChipIcon && (
                    <Chip
                      dgx={cloudProvider === "GCPALPHA"}
                      selected={true}
                      chipName={chipName}
                      manufacturerName={manufacturerName}
                      onClick={() => {}}
                      className={classNames("h-[50px] mr-3 flex-shrink-0")}
                    />
                  )}
                  <div className="flex flex-row gap-3 justify-between">
                    <div className="flex flex-col">
                      <SocketDiv className="flex flex-row justify-start items-start gap-2">
                        <div className="text-md text-primary">
                          {manufacturerName} {chipName}
                        </div>
                        <div className="text-md text-secondary">
                          {vramSize} VRAM
                        </div>
                      </SocketDiv>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-row gap-3 w-full">
                <div className="flex flex-col justify-center">
                  <SocketDiv>
                    <div className="text-xs text-secondary">
                      {ramSize} RAM x {cpuCount} CPUS
                    </div>
                  </SocketDiv>
                </div>
                <div className="flex items-center">
                  {instanceType.elastic_root_volume ? (
                    <SocketDiv>
                      <div className="flex flex-row items-center gap-2 text-secondary text-xs">
                        <CircleStackIcon className="h-4 w-4" />
                        Flexible Storage
                      </div>
                    </SocketDiv>
                  ) : (
                    <div className="flex gap-3">
                      {instanceStorage
                        .filter((storage) => storage.type_label)
                        .map((storage, idx) => (
                          <SocketDiv key={`storage-badge-${idx}`}>
                            <div className="flex flex-row items-center gap-2 text-secondary text-xs">
                              <CircleStackIcon className="h-4 w-4" />
                              {storage.size} {storage.type_label}
                            </div>
                          </SocketDiv>
                        ))}
                    </div>
                  )}
                </div>
                {instanceType.supported_gpus[0].network_details && (
                  <div className="flex items-center">
                    <SocketDiv>
                      <div className="flex flex-row items-center gap-2 text-secondary text-xs">
                        <SolidCpuChipIcon className="h-4 w-4" />
                        {instanceType.supported_gpus[0].network_details.toUpperCase()}
                      </div>
                    </SocketDiv>
                  </div>
                )}
                <div className="flex flex-col justify-center">
                  <SocketDiv>
                    <div className="flex-row items-center text-primary mt-1 hidden sm:flex">
                      <div className="flex flex-row items-center">
                        <CloudDisplay
                          provider={displayPlatformType(cloudProvider)}
                          cloud={cloud}
                          icon={<CloudIcon className="h-4 w-4 mb-1" />}
                          className="text-secondary"
                          onClick={() => {
                            window.open(
                              getCloudPlatformUrl(cloudProvider),
                              "_blank"
                            );
                          }}
                          tooltip={`Redirect to ${getCloudPlatformUrl(
                            cloudProvider
                          )}`}
                        />
                      </div>
                    </div>
                  </SocketDiv>
                </div>
              </div>
            </SocketDiv>
          </div>
          <div className="flex flex-row flex-1 w-full items-center justify-center mr-4 mt-2">
            <VentPattern />
          </div>
          <div className="flex flex-col items-center mr-4 mt-2">
            <div className="flex flex-row items-center gap-2 w-full">
              <div className="bg-primaryBg rounded-md w-3 h-10"></div>
              <div className="bg-primaryBg rounded-md w-3 h-10"></div>
              <div className="bg-primaryBg rounded-md w-3 h-10"></div>
              <div className="bg-primaryBg rounded-md w-3 h-10"></div>
              <div className="bg-primaryBg rounded-md w-3 h-10"></div>
            </div>
            <div className="flex flex-row items-center mt-2 w-full">
              {price !== "" && (
                <div className="flex flex-row items-center w-full">
                  <SocketDiv className="w-full justify-center">
                    <div className="flex flex-row items-center text-secondary justify-center">
                      <PlayCircleIcon className="h-6 w-6 mr-1" />
                      <h1 className="text-sm">${price}/hr</h1>
                    </div>
                  </SocketDiv>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col h-inherit items-center justify-between py-1">
            <StatusLights />
            <StatusLights />
            <StatusLights />
          </div>
        </div>
      </motion.div>
    </Button>
  );
};

export default RackCard;

function VentPattern() {
  return (
    <div className="h-16 bg-gradient-to-r from-primaryBg to-zinc-900 rounded border border-primaryBg relative overflow-hidden w-full">
      {/* Vent holes using repeating gradient pattern */}
      <div
        className="absolute inset-0 opacity-60"
        style={{
          background: `repeating-linear-gradient(
              90deg,
              transparent 0px,
              transparent 4px,
              #27272a 4px,
              #27272a 6px
            )`,
        }}
      ></div>
      <div
        className="absolute inset-0 opacity-40"
        style={{
          background: `repeating-linear-gradient(
              0deg,
              transparent 0px,
              transparent 2px,
              #3f3f46 2px,
              #3f3f46 3px
            )`,
        }}
      ></div>
    </div>
  );
}

function StatusLights() {
  return (
    <div
      className={`w-2 h-1 rounded-full bg-green-400 shadow-green-400/50 animate-pulse`}
      style={{
        boxShadow: "0 0 4px #4ade80",
        animationDuration: "0.5s",
      }}
    ></div>
  );
}

function StatusNob({ count }: { count: string }) {
  return (
    <div className="w-6 h-6 rounded-full bg-primaryBg flex items-center justify-center p-3">
      <div className="text-xs text-secondary">{count}</div>
    </div>
  );
}

function SocketDiv({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx(
        "flex flex-row bg-primaryBg rounded-md p-1 border border-zinc-800",
        className
      )}
    >
      {children}
    </div>
  );
}
